/**
 * PerformanceChangeLog.js
 * Change log report for performance indicators
 * Shows: student name (not ID), category description (not code), user full name (not user ID)
 * Follows Sixers branding
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  FaHistory, FaSearch, FaFilter, FaPrint,
  FaCheckCircle, FaTimesCircle, FaTimes,
  FaCalendarAlt, FaUser, FaTag, FaSortAmountDown,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import { useTheme, getT, glassCard } from "./ThemeContext";
import API_BASE from "./apiConfig";
const BASE = API_BASE;
const T = {
  blue:   Colors.primaryBlue || "#034e7b",
  yellow: Colors.logoYellow  || "#f7ce0b",
  gray:   "rgba(255,255,255,0.45)",
  glass:  "rgba(255,255,255,0.06)",
  blur:   "blur(20px)",
  bdr:    "1px solid rgba(255,255,255,0.10)",
  sh:     "0 4px 28px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.06)",
  pageBg: "linear-gradient(160deg, #020d1a 0%, #040f20 50%, #020c18 100%)",
  font:   "'Segoe UI', system-ui, sans-serif",
};

const today = () => new Date().toISOString().slice(0,10);
const monthAgo = () => { const d=new Date(); d.setDate(d.getDate()-30); return d.toISOString().slice(0,10); };

function formatDT(dt){
  if(!dt)return"—";
  const d=new Date(dt);
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+" "+
    d.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"});
}

function SLabel({children}){
  const { isDark } = useTheme();
  const T = getT(isDark);
  return(
    <div style={{display:"flex",alignItems:"center",gap:10,margin:"22px 0 14px"}}>
      <div style={{height:2,width:16,background:T.yellow,borderRadius:2}}/>
      <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.09em",textTransform:"uppercase",color:T.gray,fontFamily:T.font}}>{children}</span>
      <div style={{flex:1,height:1,background:T.divider}}/>
    </div>
  );
}

function StatCard({icon:Icon,label,value,color,sub}){
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  return(
    <div style={{...gc,padding:"18px 22px",display:"flex",alignItems:"center",gap:14}}>
      <div style={{width:44,height:44,borderRadius:12,background:`${color}18`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <Icon size={18} style={{color}}/>
      </div>
      <div>
        <div style={{fontSize:24,fontWeight:900,color,lineHeight:1,fontFamily:T.font}}>{value}</div>
        <div style={{fontSize:12,fontWeight:700,color:T.gray,fontFamily:T.font,marginTop:2}}>{label}</div>
        {sub&&<div style={{fontSize:11,color:T.gray,fontFamily:T.font}}>{sub}</div>}
      </div>
    </div>
  );
}

export default function PerformanceChangeLog({ currentUser }){
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const { config } = useAppConfig();
  const fontFam = `'${config.font_family||"Segoe UI"}', system-ui, sans-serif`;
  const primary = config.primary_color || T.blue;
  const secondary = config.secondary_color || T.yellow;

  const [rows,setRows]         = useState([]);
  const [filtered,setFiltered] = useState([]);
  const [loading,setLoading]   = useState(false);

  // Filters
  const [fromDate,setFromDate] = useState(monthAgo());
  const [toDate,setToDate]     = useState(today());
  const [studentQ,setStudentQ] = useState("");
  const [categoryQ,setCategoryQ] = useState("");
  const [userQ,setUserQ]       = useState("");
  const [categories,setCategories] = useState([]);
  const [students,setStudents] = useState([]);

  // Stats
  const [stats,setStats] = useState({total:0,unique_students:0,unique_categories:0});

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const r=await fetch(`${BASE}/batches/perf/changelog?limit=500`);
      const d=await r.json();
      const data=Array.isArray(d)?d:[];
      setRows(data);
      // Extract unique categories and students for filters
      const cats=[...new Set(data.map(r=>r.category_name).filter(Boolean))].sort();
      const stus=[...new Map(data.map(r=>[r.student_code,{code:r.student_code,name:r.student_name}])).values()].filter(s=>s.code);
      setCategories(cats); setStudents(stus);
    }finally{setLoading(false);}
  },[]);

  useEffect(()=>{load();},[load]);

  // Apply filters
  useEffect(()=>{
    let f=rows;
    if(fromDate) f=f.filter(r=>r.performance_date>=fromDate);
    if(toDate)   f=f.filter(r=>r.performance_date<=toDate);
    if(studentQ) f=f.filter(r=>r.student_name?.toLowerCase().includes(studentQ.toLowerCase())||r.student_code?.toLowerCase().includes(studentQ.toLowerCase()));
    if(categoryQ) f=f.filter(r=>r.category_name===categoryQ);
    if(userQ)    f=f.filter(r=>r.updated_by_name?.toLowerCase().includes(userQ.toLowerCase())||r.login_id?.toLowerCase().includes(userQ.toLowerCase()));
    setFiltered(f);
    const unique_students=new Set(f.map(r=>r.student_code)).size;
    const unique_categories=new Set(f.map(r=>r.category_name)).size;
    setStats({total:f.length,unique_students,unique_categories});
  },[rows,fromDate,toDate,studentQ,categoryQ,userQ]);

  const handlePrint=()=>window.print();
  const clearFilters=()=>{setStudentQ("");setCategoryQ("");setUserQ("");setFromDate(monthAgo());setToDate(today());};

  const SCORE_COLOR=(s)=>{
    if(s==null)return T.gray;
    if(s>=8)return"#10b981";
    if(s>=6)return"#3b82f6";
    if(s>=4)return"#f59e0b";
    return"#ef4444";
  };

  return(
    <div style={{background:T.pageBg,minHeight:"100vh",padding:30,fontFamily:fontFam}}>
      <div style={{maxWidth:1200,margin:"0 auto"}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
          <div>
            <h1 style={{margin:0,fontSize:26,fontWeight:800,color:T.textPrimary,display:"flex",alignItems:"center",gap:12}}>
              <FaHistory style={{color:secondary}}/> Performance Change Log
            </h1>
            <p style={{margin:"4px 0 0",color:T.gray,fontSize:13}}>Full audit trail of performance indicator updates with timestamps</p>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={load} style={{padding:"9px 18px",borderRadius:10,border:T.btnGlassBdr,background:T.btnGlassBg,backdropFilter:T.blur,color:T.btnGlassClr,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:fontFam}}>Refresh</button>
            <button onClick={handlePrint} style={{padding:"9px 18px",borderRadius:10,border:"none",background:`linear-gradient(135deg,${secondary},#e8b800)`,color:"#02304f",fontSize:13,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 14px rgba(255,200,0,0.35)",display:"flex",alignItems:"center",gap:7,fontFamily:fontFam}}>
              <FaPrint/> Print / Export
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:24}} className="print-hide">
          <StatCard icon={FaHistory}  label="Total Updates" value={stats.total} color={T.textPrimary}/>
          <StatCard icon={FaUser}     label="Students Updated" value={stats.unique_students} color="#8b5cf6"/>
          <StatCard icon={FaTag}      label="Categories Covered" value={stats.unique_categories} color="#10b981"/>
        </div>

        {/* Filters */}
        <div style={{...gc,padding:22,marginBottom:22}} className="print-hide">
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <FaFilter style={{color:T.yellow}}/>
            <span style={{fontSize:14,fontWeight:700,color:T.textSub,fontFamily:fontFam}}>Filters</span>
            <button onClick={clearFilters} style={{marginLeft:"auto",background:"transparent",border:T.btnGlassBdr,borderRadius:8,padding:"4px 12px",fontSize:12,color:T.gray,cursor:"pointer",fontFamily:fontFam}}>Clear All</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:14}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4,fontFamily:fontFam}}>From Date</label>
              <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}
                style={{width:"100%",padding:"8px 12px",borderRadius:9,border:`1.5px solid ${T.inputBdr}`,background:T.inputBg,color:T.inputText,colorScheme: isDark ? "dark" : "light",fontSize:13,fontFamily:fontFam,boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4,fontFamily:fontFam}}>To Date</label>
              <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)}
                style={{width:"100%",padding:"8px 12px",borderRadius:9,border:`1.5px solid ${T.inputBdr}`,background:T.inputBg,color:T.inputText,colorScheme: isDark ? "dark" : "light",fontSize:13,fontFamily:fontFam,boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4,fontFamily:fontFam}}>Student</label>
              <input value={studentQ} onChange={e=>setStudentQ(e.target.value)} placeholder="Search student…"
                style={{width:"100%",padding:"8px 12px",borderRadius:9,border:`1.5px solid ${T.inputBdr}`,background:T.inputBg,color:T.inputText,colorScheme: isDark ? "dark" : "light",fontSize:13,fontFamily:fontFam,boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4,fontFamily:fontFam}}>Category</label>
              <select value={categoryQ} onChange={e=>setCategoryQ(e.target.value)}
                style={{width:"100%",padding:"8px 12px",borderRadius:9,border:`1.5px solid ${T.inputBdr}`,background:T.inputBg,color:T.inputText,colorScheme: isDark ? "dark" : "light",fontSize:13,fontFamily:fontFam}}>
                <option value="">All Categories</option>
                {categories.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4,fontFamily:fontFam}}>Updated By</label>
              <input value={userQ} onChange={e=>setUserQ(e.target.value)} placeholder="Coach / staff name…"
                style={{width:"100%",padding:"8px 12px",borderRadius:9,border:`1.5px solid ${T.inputBdr}`,background:T.inputBg,color:T.inputText,colorScheme: isDark ? "dark" : "light",fontSize:13,fontFamily:fontFam,boxSizing:"border-box"}}/>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{...gc,overflow:"hidden"}}>
          {/* Print header */}
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.divider}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:15,fontWeight:800,color:T.textPrimary,fontFamily:fontFam}}>Performance Change Log — {filtered.length} records</div>
              {(fromDate||toDate)&&<div style={{fontSize:12,color:T.gray,marginTop:2}}>
                {fromDate} — {toDate} · {categoryQ||"All Categories"} · {studentQ||"All Students"}
              </div>}
            </div>
          </div>

          {loading&&<div style={{padding:40,textAlign:"center",color:T.gray,fontFamily:fontFam}}>Loading…</div>}

          {!loading&&(
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,fontFamily:fontFam}}>
                <thead>
                  <tr style={{background:primary}}>
                    {["Date","Timestamp","Student","Student ID","Category","Indicator","Score","Updated By","Comments"].map((h,i)=>(
                      <th key={i} style={{padding:"11px 14px",textAlign:"left",color:"white",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.05em",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r,i)=>(
                    <tr key={r.perf_id} style={{background:i%2===0?T.rowEven:T.rowOdd,borderBottom:T.rowBdr}}>
                      <td style={{padding:"10px 14px",color:T.gray,whiteSpace:"nowrap",fontSize:12}}>{r.performance_date||"—"}</td>
                      <td style={{padding:"10px 14px",color:T.gray,whiteSpace:"nowrap",fontSize:11}}>{r.updated_at?.replace("T"," ").slice(0,19)||"—"}</td>
                      <td style={{padding:"10px 14px",fontWeight:700,color:T.textPrimary}}>{r.student_name||"—"}</td>
                      <td style={{padding:"10px 14px",color:T.gray}}>{r.student_code||"—"}</td>
                      <td style={{padding:"10px 14px"}}>
                        <span style={{padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:"rgba(247,206,11,0.12)",color:"#f7ce0b"}}>{r.category_name||"—"}</span>
                      </td>
                      <td style={{padding:"10px 14px",color:T.textSub}}>{r.indicator_name||"—"}</td>
                      <td style={{padding:"10px 14px",textAlign:"center"}}>
                        {r.score!=null
                          ?<span style={{fontWeight:800,color:SCORE_COLOR(r.score),fontSize:14}}>{r.score}</span>
                          :<span style={{color:T.gray}}>—</span>}
                      </td>
                      <td style={{padding:"10px 14px",color:T.textSub,fontWeight:600}}>{r.updated_by_name||r.login_id||"—"}</td>
                      <td style={{padding:"10px 14px",color:T.gray,fontSize:12,maxWidth:200}}>
                        <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>{r.coach_comments||"—"}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length===0&&<div style={{padding:40,textAlign:"center",color:T.gray,fontFamily:fontFam,fontSize:14}}>No records match the current filters.</div>}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
          body { background: white !important; }
          * { box-shadow: none !important; backdrop-filter: none !important; }
        }
      `}</style>
    </div>
  );
}
